package ui.app.assign.detail

import androidx.compose.runtime.*
import api.AppType
import apps.App
import common.frontend.api.Io
import common.frontend.api.canLayerBottomDelete
import common.frontend.api.canLayerBottomEdit
import compose.assets.Svg
import compose.assets.svg.Device
import compose.assets.svg.Information_slab_circle_outline
import compose.assets.svg.List_box_outline
import io.Repository
import kotlinx.coroutines.launch
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.log.logERROR
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.TabDefinitionSimple
import tools.await
import ui.snackBarStateGlobal

@VM
class AppDetailVM : NavigationViewModel() {


    private lateinit var scopeUi: AppDetailScope

    var state by mutableDataState<AppDetailScope.Wrapper>()
        private set

    lateinit var tabs: List<TabDefinitionSimple>
        private set

    var tabSelected by mutableStateOf<TabTag>(TabTag.CONFIG_PRIMARY)
        private set

    sealed interface Action {

        data object Save : Action
        data class Archive(val doArchive: Boolean) : Action

        data class Delete(val withLogo: Boolean, val withContent: Boolean) : Action

        companion object {

            @Composable
            fun rememberAction(vm: AppDetailVM): (Action) -> Unit {
                return remember {
                    { act ->
                        when (act) {
                            Save -> vm.save()
                            is Archive -> vm.archive(act.doArchive)
                            is Delete -> vm.delete(act.withLogo, act.withContent)
                        }
                    }
                }
            }
        }
    }

    enum class TabTag {
        CONFIG_PRIMARY, CONFIG_SECONDARY, DEVICES
    }

    fun init(scopeUi: AppDetailScope) {
        this.scopeUi = scopeUi
        if (state is DataState.Result) return

        scope.launch {
            runCatching {
                val app = Repository.api.app.single(scopeUi.args.companyId, scopeUi.args.appId)
                when (val appConfig = app.data) {
                    is App.AppGroup -> refillRssLabels(app, appConfig)
                    is App.Rss,
                    is App.Image,
                    is App.None,
                    is App.Pdf,
                    is App.Video,
                    is App.VideoAlphaLoop,
                    is App.WwwEmbedded,
                    is App.WwwLink,
                    is App.Balloon,
                    is App.Gallery,
                    is App.Wheel,
                    is App.Camera,
                    is App.Maze,
                    is App.UsbPlayer,
                    is App.Kindergarten,
                    is App.Quiz -> Unit
                }

                tabs = buildList {

                    add(
                        TabDefinitionSimple(
                            icon = Svg.Information_slab_circle_outline,
                            label = Res.string.layer_bottom_editor__nav_info,
                            tag = TabTag.CONFIG_PRIMARY
                        ) { tabSelected = TabTag.CONFIG_PRIMARY })

                    if (AppType[app.type] in listOf(AppType.APP_GROUP, AppType.QUIZ, AppType.GALLERY)) {
                        add(
                            TabDefinitionSimple(
                                icon = Svg.List_box_outline,
                                label = Res.string.layer_bottom_editor__nav_selection,
                                tag = TabTag.CONFIG_SECONDARY
                            ) { tabSelected = TabTag.CONFIG_SECONDARY })
                    }

                    add(
                        TabDefinitionSimple(
                            icon = Svg.Device,
                            label = Res.string.layer_bottom_editor__nav_devices,
                            tag = TabTag.DEVICES
                        ) { tabSelected = TabTag.DEVICES })
                }

                state = DataState.Result(
                    AppDetailScope.Wrapper(
                        canEdit = scopeUi.login.canLayerBottomEdit(scopeUi.args.companyId),
                        canDelete = scopeUi.login.canLayerBottomDelete(scopeUi.args.companyId),
                        data = app
                    )
                )
            }.onFailure {
                it.printStackTrace()
                state = DataState.Error("Connection fail ${it.message}")
            }
        }
    }

    private suspend fun refillRssLabels(app: Io.AppDetail, data: App.AppGroup) {
        if (!data.allLabelsSet()) {
            data.content.setupLabels()
            runCatching {
                Repository.api.app.edit(scopeUi.args.companyId, app)
            }.onFailure {
                logERROR(it)
            }
        }
    }

    private fun save() {
        scope.launch {
            state.entry?.apply {
                runCatching {
                    Repository.api.app.edit(scopeUi.args.companyId, data)
                }.onFailure {
                    it.printStackTrace()
                    snackBarStateGlobal.showSnackbar(
                        message = it.message ?: "Fail to save",
                        withDismissAction = true
                    )
                }.onSuccess {
                    if (AppType[it.type] in arrayOf(AppType.APP_GROUP, AppType.QUIZ)) {
                        confirmChanges()
                    } else {
                        state = DataState.Result(
                            AppDetailScope.Wrapper(
                                canEdit = scopeUi.login.canLayerBottomEdit(scopeUi.args.companyId),
                                canDelete = scopeUi.login.canLayerBottomDelete(scopeUi.args.companyId),
                                data = it
                            )
                        )
                    }
                }
            }
        }
    }

    private fun archive(archive: Boolean) {
        scope.launch {
            runCatching {
                Repository.api.app.archive(
                    companyId = scopeUi.args.companyId,
                    appId = scopeUi.args.appId,
                    doArchive = archive
                )
            }.onFailure {
                snackBarStateGlobal.showSnackbar(
                    message = it.message ?: "Archivation state change failure",
                    withDismissAction = true
                )
                it.printStackTrace()
            }.onSuccess {
                scopeUi.nav.up()
            }
        }
    }

    private fun delete(withLogo: Boolean, withContent: Boolean) {
        scope.launch {
            state.entry?.data?.apply {
                runCatching {
                    Repository.api.app.delete(scopeUi.args.companyId, id, withLogo, withContent)
                }.onSuccess {
                    scopeUi.nav.up()
                }.onFailure {
                    snackBarStateGlobal.showSnackbar(
                        it.message ?: "Fail to delete",
                        withDismissAction = true
                    )
                    it.printStackTrace()
                }
            }

        }
    }

    override fun onRelease() {
        state = DataState.Idle()
    }
}


private suspend fun App.AppGroup.Content.setupLabels() {
    labelSearch = Res.string.layer_bottom_editor__app_group_label_search_default.await()
}
